import React from "react";
import { Clock, FileText, Globe, Shield, RotateCcw, Users, Zap, AlertCircle, Award } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";

const Services = () => {
  const services = [
    {
      title: "Tatkal Passport",
      description: "Get your passport issued under the urgent Tatkal scheme with faster processing and delivery.",
      icon: <Zap className="h-8 w-8 text-yellow-600" />,
      timeline: "3-7 days",
      popular: true
    },
    {
      title: "Fresh Passport",
      description: "Apply for a brand-new passport for first-time applicants.",
      icon: <FileText className="h-8 w-8 text-blue-600" />,
      timeline: "15-20 days",
      popular: false
    },
    {
      title: "Passport Renewal",
      description: "Hassle-free renewal process for expiring or expired passports.",
      icon: <RotateCcw className="h-8 w-8 text-purple-600" />,
      timeline: "15-20 days",
      popular: true
    },
    {
      title: "Urgent Passport",
      description: "Priority handling for applicants who require a passport in the shortest possible time.",
      icon: <Clock className="h-8 w-8 text-red-500" />,
      timeline: "1-2 days",
      popular: true
    },
    {
      title: "Lost Passport",
      description: "Assistance in applying for a duplicate passport if your current one has been lost or stolen.",
      icon: <AlertCircle className="h-8 w-8 text-red-600" />,
      timeline: "10-15 days",
      popular: false
    },
    {
      title: "Damaged Passport",
      description: "Replacement services for passports that are torn, water-damaged, or have lost readability.",
      icon: <Shield className="h-8 w-8 text-orange-600" />,
      timeline: "10-15 days",
      popular: false
    },
    {
      title: "Minor Passport",
      description: "Application support for passports issued to children below 18 years of age.",
      icon: <Users className="h-8 w-8 text-pink-600" />,
      timeline: "15-20 days",
      popular: false
    },
    {
      title: "ECNR Passport",
      description: "Secure your passport with Emigration Check Not Required status for overseas employment and travel.",
      icon: <Globe className="h-8 w-8 text-green-600" />,
      timeline: "15-20 days",
      popular: false
    },
    {
      title: "Police Clearance Certificate",
      description: "Official document issued by police authorities certifying that you have no criminal record.",
      icon: <Award className="h-8 w-8 text-indigo-600" />,
      timeline: "7-14 days",
      popular: false
    }
  ];

  const handleServiceClick = (serviceName) => {
    const message = encodeURIComponent(`I would like to enquire about ${serviceName}`);
    window.open(`https://wa.me/917977084003?text=${message}`, "_blank");
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Our Services</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Comprehensive passport solutions available at all locations. 
            Choose from our range of services designed to meet your specific needs.
          </p>
          <div className="mt-6">
            <Badge variant="secondary" className="text-lg px-4 py-2">
              Available at all locations
            </Badge>
          </div>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <Card 
              key={index} 
              className={`hover:shadow-lg transition-all duration-300 cursor-pointer hover:scale-105 ${service.popular ? 'ring-2 ring-blue-200' : ''}`}
              onClick={() => handleServiceClick(service.title)}
            >
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {service.icon}
                    <CardTitle className="text-lg">{service.title}</CardTitle>
                  </div>
                  {service.popular && (
                    <Badge className="bg-blue-600 text-white">Popular</Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">{service.description}</p>
                <div className="flex items-center space-x-2 text-sm text-gray-500">
                  <Clock className="h-4 w-4" />
                  <span>Processing: {service.timeline}</span>
                </div>
                <div className="mt-3 text-sm text-green-600 font-medium">
                  Click to enquire via WhatsApp
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center bg-white rounded-lg p-8 shadow-md">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Need Help Choosing?</h2>
          <p className="text-gray-600 mb-6">
            Our experts are here to guide you through the application process and help you select the right service.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://wa.me/917977084003" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-green-600 hover:bg-green-700 transition-colors"
            >
              Chat on WhatsApp
            </a>
            <a 
              href="tel:+917977084003"
              className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
            >
              Call Now
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Services;